import type { EntityCollectionAdapter, EntityState, GenericMetaOptions } from '@core/ngrx-legacy';
import { createEntityCollectionStore, ngrxEvent, pipeState } from '@core/ngrx-legacy';
import type { SkillsImport, SkillsImportCreateParams } from '@core/tellent-skills-types';

type State = EntityState<SkillsImport>;
type Adapter = EntityCollectionAdapter<SkillsImport, GenericMetaOptions>;

const insert =
  (adapter: Adapter, scopes: string[]) =>
  (state: State, { data }: { data: SkillsImport }) =>
    scopes.reduce(
      (acc, scope) =>
        pipeState(
          acc,
          s => adapter.addOne(s, data, { scope }),
          s => adapter.markAsLoaded(s, { scope, entities: [data.id] }),
        ),
      state,
    );

const { actions, selectors, reducer, namespace } = createEntityCollectionStore<SkillsImport>()(
  'SkillsImport',
  {
    customActions: adapter => ({
      createImport: ngrxEvent<SkillsImportCreateParams>(),
      createImportSuccess: insert(adapter, ['main']),
      createImportFailed: ngrxEvent<unknown>(),
      submitImport: ngrxEvent<{ importId: number; userPrompt: string | null }>(),
      submitImportSuccess: ngrxEvent<{ importId: number }>(),
      submitImportFailed: ngrxEvent<unknown>(),
      fetchImport: ngrxEvent<{ id: number }>(),
      fetchImportSuccess: insert(adapter, ['main']),
      fetchImportFailed: ngrxEvent<unknown>(),
    }),
  },
);

const SkillsImportActions: Pick<
  typeof actions,
  | 'createImport'
  | 'createImportSuccess'
  | 'createImportFailed'
  | 'submitImport'
  | 'submitImportSuccess'
  | 'submitImportFailed'
  | 'fetchImport'
  | 'fetchImportSuccess'
  | 'fetchImportFailed'
> = actions;

export {
  reducer as SkillsImportReducer,
  namespace as SkillsImportNamespace,
  SkillsImportActions,
  selectors as SkillsImportSelectors,
};
